/**
 * CancelCollectionOfferModal - Modal for cancelling a collection offer
 *
 * Flow:
 * 1. User confirms they want to cancel
 * 2. Backend marks offer as cancelled and returns EscrowCancel payload
 * 3. User signs EscrowCancel via Xaman to reclaim escrowed funds
 * 4. Once signed, mark the cancellation as complete
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Dialog } from '../../ui/components/Dialog';
import { Button } from '../../ui/components/Button';
import { FeatherX, FeatherCheck, FeatherAlertTriangle, FeatherInfo } from '@subframe/core';
import {
  cancelCollectionOffer,
  markOfferCancelled,
  type CollectionOffer,
} from '../../api/collectionOffers';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore } from '../../store/notificationStore';
import { formatPrice } from '../../utils/formatters';
import { format } from 'date-fns';
import { WalletSigningModal } from '../transactions/WalletSigningModal';
import type { SigningPayload } from '../../types/signing';

interface CancelCollectionOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  offer: CollectionOffer;
  onSuccess?: () => void;
}

type ModalStep = 'confirm' | 'signing' | 'success' | 'error';

export const CancelCollectionOfferModal: React.FC<CancelCollectionOfferModalProps> = ({
  isOpen,
  onClose,
  offer,
  onSuccess,
}) => {
  const { user } = useAuthStore();
  const { success: showSuccess } = useNotificationStore();

  const [step, setStep] = useState<ModalStep>('confirm');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const [fundsReclaimed, setFundsReclaimed] = useState<boolean>(false);

  // Normalized signing payload (any provider). When set, WalletSigningModal owns the
  // signing lifecycle (Xaman QR / deep link, or WalletConnect approval for joey-wc).
  const [signing, setSigning] = useState<SigningPayload | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('confirm');
      setError('');
      setLoading(false);
      setSigning(null);
      setFundsReclaimed(false);
    }
  }, [isOpen]);

  // WalletSigningModal resolved the signing in our favor. Finalize the cancellation.
  const handleSignedSuccess = useCallback(async (txid?: string) => {
    setSigning(null);

    try {
      await markOfferCancelled({ offerId: offer.id, transactionHash: txid });
      setFundsReclaimed(true);
      setStep('success');
      showSuccess('Collection offer cancelled. Your funds have been returned.');
    } catch {
      // Even if marking fails, the escrow is cancelled on-chain
      setFundsReclaimed(true);
      setStep('success');
      showSuccess('Collection offer cancelled. Your funds have been returned.');
    }
  }, [offer.id, showSuccess]);

  const handleRejected = useCallback(() => {
    setSigning(null);
    setError('Transaction was rejected. Your offer is still active.');
    setStep('error');
  }, []);

  const handleExpired = useCallback(() => {
    setSigning(null);
    setError('Transaction request expired. Please try again.');
    setStep('error');
  }, []);

  // Check if escrow is still locked (before expiration)
  const isEscrowLocked = offer.expiresAt && new Date(offer.expiresAt) > new Date();

  const handleCancel = async () => {
    if (!user?.xrpl_address) return;

    setError('');
    setLoading(true);

    try {
      if (isEscrowLocked) {
        // Escrow is still locked - just mark as cancelled in our DB
        // Funds will be automatically returned after expiration by our cron job
        await markOfferCancelled({ offerId: offer.id });
        setStep('success');
        showSuccess('Collection offer cancelled. Your funds will be automatically returned after the offer expires.');
      } else {
        // Escrow can be cancelled - proceed with signing flow
        const signingPayload = await cancelCollectionOffer({
          offerId: offer.id,
          userAddress: user.xrpl_address,
        });

        setSigning(signingPayload);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel offer');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (step === 'success') {
      onSuccess?.();
    }
    onClose();
  };

  const getTitle = () => {
    switch (step) {
      case 'confirm': return 'Cancel Collection Offer';
      case 'signing': return 'Sign to Reclaim Funds';
      case 'success': return 'Offer Cancelled';
      case 'error': return 'Error';
      default: return 'Cancel Offer';
    }
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Content className="w-full max-w-[420px]">
        <div className="flex w-full flex-col items-start gap-6 p-6">
          {/* Header */}
          <div className="flex w-full items-start justify-between">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {getTitle()}
            </span>
            <button
              type="button"
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded hover:bg-neutral-100"
            >
              <FeatherX className="h-5 w-5 text-subtext-color" />
            </button>
          </div>

          {/* Confirm Step */}
          {step === 'confirm' && (
            <div className="flex flex-col gap-4 w-full">
              {/* Check if escrow can be cancelled now */}
              {offer.expiresAt && new Date(offer.expiresAt) > new Date() ? (
                // Escrow is still locked
                <div className="bg-warning-50 border border-warning-200 rounded-md p-4 flex items-start gap-3">
                  <FeatherAlertTriangle className="h-5 w-5 text-warning-600 flex-shrink-0 mt-0.5" />
                  <div className="flex flex-col gap-1">
                    <span className="text-body-bold text-warning-700">Funds are locked until expiration</span>
                    <span className="text-caption text-warning-600">
                      Your escrowed funds ({formatPrice(offer.escrowAmountXrp || offer.displayAmountXrp * 1.01)}) are locked on the XRP Ledger until this offer expires.
                    </span>
                  </div>
                </div>
              ) : (
                // Escrow can be cancelled
                <div className="bg-warning-50 border border-warning-200 rounded-md p-4 flex items-start gap-3">
                  <FeatherAlertTriangle className="h-5 w-5 text-warning-600 flex-shrink-0 mt-0.5" />
                  <div className="flex flex-col gap-1">
                    <span className="text-body-bold text-warning-700">Are you sure?</span>
                    <span className="text-caption text-warning-600">
                      This will cancel your collection offer and return your escrowed funds.
                    </span>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-2 p-4 bg-neutral-50 rounded-md">
                <div className="flex justify-between text-body">
                  <span className="text-subtext-color">Offer Amount</span>
                  <span className="text-default-font font-medium">{formatPrice(offer.displayAmountXrp)}</span>
                </div>
                <div className="flex justify-between text-body">
                  <span className="text-subtext-color">Escrowed Amount</span>
                  <span className="text-default-font font-medium">{formatPrice(offer.escrowAmountXrp || offer.displayAmountXrp * 1.01)}</span>
                </div>
                {offer.expiresAt && (
                  <div className="flex justify-between text-body">
                    <span className="text-subtext-color">Funds Available</span>
                    <span className="text-default-font font-medium">
                      {new Date(offer.expiresAt) > new Date()
                        ? format(new Date(offer.expiresAt), 'MMM d, yyyy h:mm a')
                        : 'Now'}
                    </span>
                  </div>
                )}
                <div className="h-px bg-neutral-border my-1" />
                <div className="flex justify-between text-body-bold">
                  <span className="text-default-font">You'll Receive Back</span>
                  <span className="text-success-600">{formatPrice(offer.escrowAmountXrp || offer.displayAmountXrp * 1.01)}</span>
                </div>
              </div>

              {offer.expiresAt && new Date(offer.expiresAt) > new Date() ? (
                <div className="bg-neutral-100 border border-neutral-200 rounded-md p-3 flex items-start gap-2">
                  <FeatherInfo className="h-4 w-4 text-subtext-color flex-shrink-0 mt-0.5" />
                  <p className="text-caption text-subtext-color">
                    Cancelling now will hide this offer from sellers, but your funds will remain locked in escrow until{' '}
                    <span className="font-medium">{format(new Date(offer.expiresAt), 'MMM d, yyyy h:mm a')}</span>.
                    Your funds will be automatically returned to your wallet after this time.
                  </p>
                </div>
              ) : (
                <p className="text-caption text-subtext-color">
                  You'll be asked to sign an EscrowCancel transaction to release your funds back to your wallet.
                </p>
              )}
            </div>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <div className="flex flex-col items-center gap-4 py-4 w-full">
              <div className="w-16 h-16 rounded-full bg-success-100 flex items-center justify-center">
                <FeatherCheck className="w-8 h-8 text-success-600" />
              </div>
              <p className="text-body-bold text-default-font text-center">
                Offer Cancelled Successfully
              </p>
              {fundsReclaimed ? (
                <p className="text-body text-subtext-color text-center">
                  Your escrowed funds ({formatPrice(offer.escrowAmountXrp || offer.displayAmountXrp * 1.01)}) have been returned to your wallet.
                </p>
              ) : (
                <div className="flex flex-col gap-2 text-center">
                  <p className="text-body text-subtext-color">
                    Your offer has been cancelled and will no longer be shown to sellers.
                  </p>
                  <p className="text-caption text-subtext-color">
                    Your escrowed funds ({formatPrice(offer.escrowAmountXrp || offer.displayAmountXrp * 1.01)}) will be automatically returned to your wallet after{' '}
                    {offer.expiresAt ? format(new Date(offer.expiresAt), 'MMM d, yyyy h:mm a') : 'the offer expires'}.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Error Step */}
          {step === 'error' && (
            <div className="flex flex-col items-center gap-4 py-4 w-full">
              <div className="w-16 h-16 rounded-full bg-error-100 flex items-center justify-center">
                <FeatherX className="w-8 h-8 text-error-600" />
              </div>
              <p className="text-body text-error-600 text-center">
                {error || 'Something went wrong'}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex w-full items-center justify-end gap-3">
            {step === 'confirm' && (
              <>
                <Button variant="neutral-secondary" onClick={handleClose} disabled={loading}>
                  Keep Offer
                </Button>
                <Button
                  variant="destructive-primary"
                  onClick={handleCancel}
                  disabled={loading}
                  loading={loading}
                >
                  Cancel Offer
                </Button>
              </>
            )}

            {(step === 'success' || step === 'error') && (
              <Button variant="neutral-secondary" onClick={handleClose}>
                Close
              </Button>
            )}
          </div>
        </div>
      </Dialog.Content>
    </Dialog>

    {signing && (
      <WalletSigningModal
        open={true}
        onClose={() => setSigning(null)}
        title="Sign to Reclaim Funds"
        payload={signing}
        onSuccess={handleSignedSuccess}
        onRejected={handleRejected}
        onExpired={handleExpired}
      />
    )}
    </>
  );
};
